import { Progress, Modal, Stack, Text, ScrollArea, Box } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { calculatePercentage } from '@dealstack/frontend/shared/utils/calculatePercentage';
import { useEffect, useState, useRef } from 'react';
import { POLL_FOR_UPDATE_PROGRESS_IN_MS } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/constants';
import { useWorkflowProgress } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/WorkflowProgress/WorkflowProgressContext';
import {
	getInvestorEmailWorkflowProgress,
	getInvestorReleaseWorkflowProgress,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useQuery } from '@tanstack/react-query';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';

interface Props {
	className?: string;
	refreshInvestors: () => void;
}

const PROGRESS_LABELS = {
	email: {
		title: 'investors.progress.sending_investor_emails',
		subtitle: 'investors.progress.sent',
	},
	release: {
		title: 'investors.progress.releasing_investors',
		subtitle: 'investors.progress.released',
	},
} as const;

export const ProgressBar = ({ refreshInvestors }: Props) => {
	const { mipId } = useRequiredParams('mipId');
	const tMipManage = useTranslationMipManage();
	const {
		emailHandlerId,
		releaseHandlerId,
		lastStartedKind,
		finishEmailWorkflow,
		finishReleaseWorkflow,
	} = useWorkflowProgress();
	const [modalOpened, setModalOpened] = useState(false);
	const [progress, setProgress] = useState(0);
	const [total, setTotal] = useState(0);
	const [totalFailed, setTotalFailed] = useState(0);
	const [completed, setCompleted] = useState(false);
	const [showCloseBtn, setShowCloseBtn] = useState(false);
	const [failedEmailsAddresses, setFailedEmailsAddresses] = useState<string[]>([]);
	const color = progress < 99 ? 'blue' : '#00973c';
	const prevEmailHandlerIdRef = useRef<string | null>(null);
	const prevReleaseHandlerIdRef = useRef<string | null>(null);
	const hasInitializedRef = useRef(false);

	const labels = lastStartedKind ? PROGRESS_LABELS[lastStartedKind] : null;
	const title = labels ? tMipManage(labels.title) : '';
	const subtitle = labels ? tMipManage(labels.subtitle) : '';

	// Open modal when a handler appears (on mount if one is already running, or on the null -> value transition)
	useEffect(() => {
		if (!hasInitializedRef.current) {
			hasInitializedRef.current = true;
			if (emailHandlerId || releaseHandlerId) {
				setModalOpened(true);
			}
			prevEmailHandlerIdRef.current = emailHandlerId;
			prevReleaseHandlerIdRef.current = releaseHandlerId;
			return;
		}

		if (!prevEmailHandlerIdRef.current && emailHandlerId) {
			setModalOpened(true);
		}
		if (!prevReleaseHandlerIdRef.current && releaseHandlerId) {
			setModalOpened(true);
		}

		prevEmailHandlerIdRef.current = emailHandlerId;
		prevReleaseHandlerIdRef.current = releaseHandlerId;
	}, [emailHandlerId, releaseHandlerId]);

	const onError = () => {
		setShowCloseBtn(true);
	};

	const emailProgressQ = useQuery({
		queryKey: [
			formatUnicorn(API_PATHS.MIP_EMAIL_WORKFLOW_PROGRESS_HANDLERID_GET, {
				handlerId: emailHandlerId || '',
			}),
		],
		queryFn: ({ signal }) =>
			getInvestorEmailWorkflowProgress(
				formatUnicorn(API_PATHS.MIP_EMAIL_WORKFLOW_PROGRESS_HANDLERID_GET, {
					handlerId: emailHandlerId || '',
				}),
				signal
			),
		enabled: !!emailHandlerId,
		refetchInterval: POLL_FOR_UPDATE_PROGRESS_IN_MS,
		// Polling errors are handled below (show close button); no global toast.
		meta: { suppressErrorToast: true },
	});

	useEffect(() => {
		if (emailProgressQ.isError) {
			onError();
		}
	}, [emailProgressQ.isError]);

	useEffect(() => {
		if (!emailProgressQ.isSuccess) {
			return;
		}
		const response = emailProgressQ.data;
		if (response.payload) {
			const {
				activeEmailsSent,
				inactiveEmailsSent,
				totalActiveUsers,
				totalInActiveUsers,
				inactiveEmailsFailed,
				activeEmailsFailed,
				completed,
				inactiveEmailsFailedAddresses,
				activeEmailsFailedAddresses,
			} = response.payload;

			const currentProgress = activeEmailsSent + inactiveEmailsSent;
			const currentTotal = totalActiveUsers + totalInActiveUsers;
			const currentFailed = inactiveEmailsFailed + activeEmailsFailed;

			setProgress(currentProgress);
			setTotal(currentTotal);
			setTotalFailed(currentFailed);
			setCompleted(completed);
			setFailedEmailsAddresses([
				...inactiveEmailsFailedAddresses,
				...activeEmailsFailedAddresses,
			]);
			refreshInvestors();

			if (completed) {
				finishEmailWorkflow();
				setShowCloseBtn(true);
			}
		} else {
			// on error stop calling the endpoint
			finishEmailWorkflow();
		}
	}, [emailProgressQ.isSuccess, emailProgressQ.data]);

	const releaseProgressQ = useQuery({
		queryKey: [
			formatUnicorn(
				API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_RELEASE_WORKFLOW_PROGRESS_HANDLERID_GET,
				{
					mipId,
					handlerId: releaseHandlerId || '',
				}
			),
		],
		queryFn: ({ signal }) =>
			getInvestorReleaseWorkflowProgress(
				formatUnicorn(
					API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_RELEASE_WORKFLOW_PROGRESS_HANDLERID_GET,
					{
						mipId,
						handlerId: releaseHandlerId || '',
					}
				),
				signal
			),

		enabled: !!releaseHandlerId && !!mipId,
		refetchInterval: POLL_FOR_UPDATE_PROGRESS_IN_MS,
		// Polling errors are handled below (show close button); no global toast.
		meta: { suppressErrorToast: true },
	});

	useEffect(() => {
		if (releaseProgressQ.isError) {
			onError();
		}
	}, [releaseProgressQ.isError]);

	useEffect(() => {
		if (!releaseProgressQ.isSuccess) {
			return;
		}
		const response = releaseProgressQ.data;
		if (response.payload) {
			const { completed, investorsReleased, totalInvestors } = response.payload;

			setProgress(investorsReleased);
			setTotal(totalInvestors);

			refreshInvestors();

			if (completed) {
				finishReleaseWorkflow();
				setShowCloseBtn(true);
			}
		} else {
			// on error stop calling the endpoint
			finishReleaseWorkflow();
		}
	}, [releaseProgressQ.isSuccess, releaseProgressQ.data]);

	const percentageComplete = !completed ? calculatePercentage(progress, total) : 100;
	return (
		<Modal
			opened={modalOpened}
			onClose={() => {
				if (showCloseBtn) {
					setModalOpened(false);
					setShowCloseBtn(false);
				}
			}}
			title={title}
			size="md"
			centered
			closeOnClickOutside={false}
			closeOnEscape={false}
			withCloseButton={showCloseBtn || completed}
			closeButtonProps={{
				icon: (
					<IconX
						size={20}
						stroke={1.5}
						data-testid={
							TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_TABLE_BULK_INVITE_CLOSE_BUTTON
						}
					/>
				),
			}}
		>
			<Stack gap="md">
				<Text size="sm">
					{`${progress} / ${total} `} {subtitle}
				</Text>
				<Progress color={color} value={percentageComplete} size="lg" />
				<Text
					size="sm"
					fw={700}
					ta="right"
					data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_TABLE_BULK_INVITE_PROGRESS}
				>
					{!completed && `${percentageComplete.toFixed(0)}% `}
					{completed && `100% `}
					{tMipManage('investors.progress.completed')}
				</Text>
				{completed && totalFailed > 0 && (
					<>
						<Text size="sm" fw={500}>
							{tMipManage('investors.progress.failed_emails', { count: totalFailed })}
						</Text>
						<Box p="xs">
							<ScrollArea h={200} type="scroll">
								<Stack gap="xs">
									{failedEmailsAddresses.map((email) => (
										<Text
											key={email}
											size="sm"
											style={{ wordBreak: 'break-all' }}
										>
											{email}
										</Text>
									))}
								</Stack>
							</ScrollArea>
						</Box>
					</>
				)}
			</Stack>
		</Modal>
	);
};
